import { type ReactElement, useState } from 'react'

import styles from 'components/chat-components/AgentMessage.module.css'
import { SECONDS_PER_MINUTE } from 'utils/units'

import type { YouTubeVideoContent } from 'components/chat-components/AgentMessage.types'

type YouTubeVideoRendererProperties = {
  content: YouTubeVideoContent
}

const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/
const TIMESTAMP_DIGIT_WIDTH = 2

const isContentPending = (content: YouTubeVideoContent): boolean => {
  return 'pending' in content && content.pending === true
}

const isValidYouTubeVideoId = (videoId: string): boolean => YOUTUBE_VIDEO_ID_PATTERN.test(videoId)

const buildEmbedUrl = (content: YouTubeVideoContent): string => {
  const parameters = new URLSearchParams({
    modestbranding: '1',
    playsinline: '1',
    rel: '0',
    start: String(Math.max(0, Math.floor(content.startSeconds))),
  })

  if (content.endSeconds > content.startSeconds) {
    parameters.set('end', String(Math.floor(content.endSeconds)))
  }

  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(content.videoId)}?${parameters.toString()}`
}

const formatSeconds = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE)
  const remainingSeconds = totalSeconds % SECONDS_PER_MINUTE
  return `${String(minutes).padStart(TIMESTAMP_DIGIT_WIDTH, '0')}:${String(remainingSeconds).padStart(
    TIMESTAMP_DIGIT_WIDTH,
    '0'
  )}`
}

export const YouTubeVideoRenderer = ({ content }: YouTubeVideoRendererProperties): ReactElement => {
  /* --- Section 2: Local State --- */
  const [loadedEmbedUrl, setLoadedEmbedUrl] = useState<string | undefined>(undefined)

  /* --- Section 3: Derived Values --- */
  const isPending = isContentPending(content)
  const canRenderVideo = !isPending && isValidYouTubeVideoId(content.videoId)
  const embedUrl = canRenderVideo ? buildEmbedUrl(content) : undefined
  const clipRange = `${formatSeconds(content.startSeconds)}-${formatSeconds(content.endSeconds)}`
  const isFrameReady = embedUrl !== undefined && loadedEmbedUrl === embedUrl
  const iframeClassName = isFrameReady ? styles.youTubeVideoEmbed : styles.youTubeVideoEmbedLoading

  /* --- Section 6: Render --- */
  return (
    <article className={styles.contentYouTubeVideo}>
      <div className={styles.youTubeVideoFrame}>
        {canRenderVideo ? (
          <>
            <iframe
              className={iframeClassName}
              title={content.title}
              src={embedUrl}
              loading="lazy"
              onLoad={() => setLoadedEmbedUrl(embedUrl)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
            {isFrameReady ? undefined : (
              <div className={styles.youTubeVideoLoadingOverlay} aria-busy="true" aria-label="Loading YouTube clip">
                <span className={styles.youTubeVideoSpinner} />
              </div>
            )}
          </>
        ) : (
          <div className={styles.youTubeVideoSkeleton} aria-busy="true" aria-label="Loading YouTube clip">
            <span className={styles.youTubeVideoSpinner} />
          </div>
        )}
      </div>
      <div className={styles.youTubeVideoMeta}>
        <a className={styles.youTubeVideoTitle} href={content.sourceUrl} target="_blank" rel="noreferrer">
          {content.title || 'YouTube reference'}
        </a>
        <div className={styles.youTubeVideoDetails}>
          {content.channelTitle ? `${content.channelTitle} · ` : ''}
          {clipRange}
        </div>
        {content.reason ? <p className={styles.youTubeVideoReason}>{content.reason}</p> : undefined}
      </div>
    </article>
  )
}
